import {
  Box,
  Button,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Td,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import useActions from "../../hooks/useActions";
import {
  changeUploadRequest,
  deleteSeries,
  enableDisableSeries,
  getAllUsers,
  userSearch,
} from "../../utils/api";
import useStore from "../../utils/store";
import DeleteModal from "../common/Modal/DeleteModal";
import CreateModal from "../common/Modal/Modal";
import CreateTable from "../common/Table/Table";
import Loading from "../common/ui/Loading";
import Table from "../podcasts/Table";
import ModalSub from "../subscription/Modal";
import TableSub from "../subscription/Table";

const TableSeries = ({ series, actions, reload }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpen2,
    onOpen: onOpen2,
    onClose: onClose2,
  } = useDisclosure();
  const [data2, setData2] = React.useState([]);
  const [isloading, setLoading] = React.useState(false);
  const [data, setData] = React.useState({});
  const [localSeries, setLocalSeries] = React.useState(series);
  const user = useStore((state) => state.user);
  const toast = useToast();
  const canAssignSeries = Boolean(
    user?.updateSeries || user?.getSeries || user?.addSeries || user?.uploadAccess
  );
  const actionsI =
    actions && (user?.updateSeries || user?.deleteSeries || canAssignSeries)
      ? "Actions"
      : false;
  const navigate = useNavigate();
  const [updateAction, deleteAction] = useActions();

  const {
    isOpen: isAssignOpen,
    onOpen: onAssignOpen,
    onClose: onAssignClose,
  } = useDisclosure();
  const [assignSeriesId, setAssignSeriesId] = React.useState("");
  const [userSearchInput, setUserSearchInput] = React.useState("");
  const [assignUsers, setAssignUsers] = React.useState([]);
  const [assignUsersLoading, setAssignUsersLoading] = React.useState(false);
  const [assignPage, setAssignPage] = React.useState(1);
  const [hasMoreUsers, setHasMoreUsers] = React.useState(true);
  const [assignLoading, setAssignLoading] = React.useState(false);
  const searchTimeoutRef = React.useRef(null);

  const getUserSearchType = (query) => {
    const q = (query || "").trim();
    if (q.includes("@")) return "Email";
    if (/^[+\d][\d\s-]*$/.test(q)) return "Phone";
    return "Name";
  };

  const fetchAssignUsers = async (search = "", page = 1, reset = false) => {
    setAssignUsersLoading(true);
    try {
      const query = search.trim();
      const [success, response] = query
        ? await userSearch({
            limit: 30,
            page: page,
            searchType: getUserSearchType(query),
            searchQuery: query,
          })
        : await getAllUsers(page, 30);

      if (success) {
        const newUsers = response?.users || response?.data?.users || [];
        const totalData = response?.totalData ?? response?.data?.totalData ?? 0;
        const loadedCount = (reset ? 0 : assignUsers.length) + newUsers.length;

        if (reset) {
          setAssignUsers(newUsers);
        } else {
          setAssignUsers((prev) => [...prev, ...newUsers]);
        }
        setHasMoreUsers(
          totalData ? loadedCount < totalData : newUsers.length === 30
        );
        setAssignPage(page);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load users",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setAssignUsersLoading(false);
    }
  };

  const openAssignModal = (seriesId) => {
    setAssignSeriesId(seriesId);
    setUserSearchInput("");
    setAssignPage(1);
    setAssignUsers([]);
    setHasMoreUsers(true);
    onAssignOpen();
    fetchAssignUsers("", 1, true);
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setUserSearchInput(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setAssignPage(1);
      fetchAssignUsers(value, 1, true);
    }, 450);
  };

  const loadMoreUsers = () => {
    if (!assignUsersLoading && hasMoreUsers) {
      fetchAssignUsers(userSearchInput, assignPage + 1, false);
    }
  };

  const handleAssignUser = async (userId) => {
    setAssignLoading(true);
    const [success] = await changeUploadRequest(
      assignSeriesId,
      userId,
      "series"
    );
    setAssignLoading(false);

    if (success) {
      toast({
        title: "User Assigned",
        description: "Series uploader updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onAssignClose();
      reload?.();
    } else {
      toast({
        title: "Error",
        description: "Failed to assign user.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (!isAssignOpen) {
      setAssignUsers([]);
      setAssignPage(1);
      setHasMoreUsers(true);
    }
  }, [isAssignOpen]);

  // Sync local state with prop when series changes
  React.useEffect(() => {
    setLocalSeries(series);
  }, [series]);
  if (isloading) {
    return <Loading />;
  }
  return (
    <>
      <CreateTable
        searchby={["name", "language", "ageLimit", "rating"]}
        pagging={true}
        head={[
          actionsI,
          "Name",
          "Views",
          "Description",
          "Subscription",
          "Horizontal-Banner",
          "Vertical-Banner",
          "Duration",
          "Age-Limit",
          "Rating",
          "Episodes",
          "cast",
          "Genres",
          "Uploaded By",
        ]}
        rows={(row) => {
          // Get current item from localSeries to ensure we have the latest state
          const currentItem =
            localSeries.find((item) => item._id === row._id) || row;
          return (
            <>
              {actions &&
                (user?.updateSeries || user?.deleteSeries || canAssignSeries) && (
                <Td>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: "column",
                    }}
                  >
                    {canAssignSeries && (
                      <Button
                        size="sm"
                        colorScheme="purple"
                        variant="solid"
                        onClick={() => openAssignModal(row._id)}
                      >
                        {currentItem?.uploadedBy ? "Change Uploader" : "Assign"}
                      </Button>
                    )}
                    <ModalSub item={currentItem} reload={reload} series />
                    {user?.deleteSeries && (
                      <DeleteModal
                        onClick={async () => {
                          setLoading(true);
                          await deleteAction(row._id, deleteSeries);
                          reload();
                          setLoading(false);
                        }}
                      />
                    )}
                    {user?.updateSeries && (
                      <>
                        <Button
                          colorScheme={"blue"}
                          onClick={() =>
                            navigate(`/series/update/${row._id}`, {
                              state: currentItem,
                            })
                          }
                        >
                          Edit
                        </Button>
                        <Button
                          colorScheme={currentItem.isEnable ? "red" : "green"}
                          variant="solid"
                          onClick={async () => {
                            const newIsEnable = !currentItem.isEnable;
                            // Optimistically update local state
                            setLocalSeries((prevSeries) =>
                              prevSeries.map((item) =>
                                item._id === row._id
                                  ? { ...item, isEnable: newIsEnable }
                                  : item
                              )
                            );
                            const success = await updateAction(
                              {
                                isEnable: newIsEnable,
                              },
                              row._id,
                              enableDisableSeries
                            );
                            // Reload to get fresh data from server
                            if (success) {
                              await reload();
                            } else {
                              // Revert optimistic update on failure
                              setLocalSeries((prevSeries) =>
                                prevSeries.map((item) =>
                                  item._id === row._id
                                    ? { ...item, isEnable: !newIsEnable }
                                    : item
                                )
                              );
                            }
                          }}
                          sx={{
                            marginTop: "10px",
                            width: "100%",
                          }}
                        >
                          {currentItem.isEnable ? "Disable" : "Enable"}
                        </Button>

                        <Button
                          colorScheme={currentItem.isFree ? "red" : "green"}
                          variant="solid"
                          onClick={async () => {
                            const newIsFree = !currentItem.isFree;
                            // Optimistically update local state
                            setLocalSeries((prevSeries) =>
                              prevSeries.map((item) =>
                                item._id === row._id
                                  ? { ...item, isFree: newIsFree }
                                  : item
                              )
                            );
                            const success = await updateAction(
                              {
                                isFree: newIsFree,
                              },
                              row._id,
                              enableDisableSeries
                            );
                            // Reload to get fresh data from server
                            if (success) {
                              await reload();
                            } else {
                              // Revert optimistic update on failure
                              setLocalSeries((prevSeries) =>
                                prevSeries.map((item) =>
                                  item._id === row._id
                                    ? { ...item, isFree: !newIsFree }
                                    : item
                                )
                              );
                            }
                          }}
                          sx={{
                            marginTop: "10px",
                            width: "100%",
                          }}
                        >
                          {currentItem.isFree ? "Paid" : "Free"}
                        </Button>
                      </>
                    )}
                  </Box>
                </Td>
              )}
              <Td>{row.name}</Td>
              <Td>{row.view}</Td>
              <Td>{row.description ? `${row.description.slice(0, 25)}...` : ""}</Td>{" "}
              <Td>
                <Button
                  colorScheme={"blue"}
                  onClick={() => {
                    onOpen2();
                    setData2(row.subscriptions);
                  }}
                >
                  View
                </Button>
              </Td>
              <Td>
                <Image
                  src={row.horizontalBanner}
                  alt="horizontal-banner"
                  sx={{
                    width: "100px",
                    height: "100px",
                  }}
                  rounded="md"
                />
              </Td>
              <Td>
                <Image
                  src={row.verticalBanner}
                  alt="vertical-banner"
                  sx={{
                    width: "100px",
                    height: "100px",
                  }}
                  rounded="md"
                />
              </Td>
              <Td>{row.duration}</Td>
              <Td>{row.ageLimit}</Td>
              <Td>{row.rating}</Td>
              <Td>
                <Button
                  colorScheme="blue"
                  variant="solid"
                  onClick={() => {
                    setData(
                      localSeries.filter((item) => item._id === row._id)[0]
                    );
                    onOpen();
                  }}
                >
                  View
                </Button>
              </Td>
              <Td>
                {row.cast?.map((item, index) => {
                  return <p key={index}>{item.name}</p>;
                })}
              </Td>
              <Td>
                {row.genres?.map((item, index) => {
                  return <p key={index}>{item.name}</p>;
                })}
              </Td>
              <Td>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="0.4rem"
                  minW="120px"
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {currentItem?.uploadedBy ? (
                      <>
                        {currentItem?.uploadedBy?.name}
                        <br />
                        {currentItem?.uploadedBy?.email || ""}
                        <br />
                        {currentItem?.uploadedBy?.phoneNumber || ""}
                      </>
                    ) : (
                      "Not assigned"
                    )}
                  </Text>
                  {actions && canAssignSeries && (
                    <Button
                      size="xs"
                      colorScheme="purple"
                      variant="outline"
                      onClick={() => openAssignModal(row._id)}
                    >
                      {currentItem?.uploadedBy ? "Change" : "Assign"}
                    </Button>
                  )}
                </Box>
              </Td>
            </>
          );
        }}
        data={localSeries}
      />{" "}
      <CreateModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setData({});
        }}
        table
        title="Episodes"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Table allPodcasts={data?.episodes} />
        </Box>
      </CreateModal>
      <CreateModal
        isOpen={isOpen2}
        onClose={() => {
          onClose2();
          setData2([]);
        }}
        title={"View Subscribtions"}
        table
      >
        <TableSub subscribtions={data2} />
      </CreateModal>

      <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Uploader</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Search by name, email or phone..."
              value={userSearchInput}
              onChange={handleSearchInput}
              mb={4}
            />

            {assignUsersLoading && assignUsers.length === 0 ? (
              <Box display="flex" justifyContent="center" py={8}>
                <Spinner />
              </Box>
            ) : (
              <>
                <VStack
                  align="stretch"
                  maxH="420px"
                  overflowY="auto"
                  spacing={2}
                >
                  {assignUsers.length === 0 ? (
                    <Text color="gray.500" textAlign="center" py={8}>
                      {userSearchInput.trim()
                        ? "No users found"
                        : "Type to search users..."}
                    </Text>
                  ) : (
                    assignUsers.map((u) => (
                      <Box
                        key={u._id}
                        p={3}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{
                          bg: "purple.50",
                          borderColor: "purple.300",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          !assignLoading && handleAssignUser(u._id)
                        }
                      >
                        <Text fontWeight="semibold" fontSize="sm">
                          {u.name || "—"}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {u.email}{" "}
                          {u.phoneNumber || u.phone
                            ? `• ${u.phoneNumber || u.phone}`
                            : ""}
                        </Text>
                      </Box>
                    ))
                  )}
                </VStack>

                {hasMoreUsers && (
                  <Button
                    mt={4}
                    width="full"
                    variant="outline"
                    onClick={loadMoreUsers}
                    isLoading={assignUsersLoading}
                  >
                    Load More Users
                  </Button>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              onClick={onAssignClose}
              isDisabled={assignLoading}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TableSeries;
